import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Stack,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
} from '@mui/material';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { SetRentDetailsDto } from '@/types/dto/rent-unit.dto';

import { useGetRentDetails } from '@/api/endpoints/rent/useGetRentDetails';
import { useGetRentPrices } from '@/api/endpoints/rent/useGetRentPrices';
import { useSetRentDetails } from '@/api/endpoints/rent/useSetRentDetails';

import FormTextField from '@/components/form/FormTextField';

interface RentDetailsFormValues {
  rentAmount: number;
  rentCurrency: string;
  rentInterval: 'month' | 'year';
}

interface RentDetailsFormSectionProps {
  unitId: number;
  onRentUpdate: (isNowDefined: boolean) => void;
}

const RentDetailsFormSection: React.FC<RentDetailsFormSectionProps> = ({
  unitId,
  onRentUpdate,
}) => {
  const { data: currentRentDetails, isLoading: isLoadingRent } = useGetRentDetails(
    unitId,
    !!unitId
  );

  const { data: rentPrices = [], isLoading: isLoadingPrices } = useGetRentPrices(unitId, !!unitId);

  const rentDetailsForm = useForm<RentDetailsFormValues>({
    defaultValues: {
      rentAmount: 0,
      rentCurrency: 'usd',
      rentInterval: 'month',
    },
    mode: 'onChange',
  });

  useEffect(() => {
    if (currentRentDetails) {
      const validInterval =
        currentRentDetails.interval === 'month' || currentRentDetails.interval === 'year'
          ? currentRentDetails.interval
          : 'month';

      rentDetailsForm.reset({
        rentAmount: currentRentDetails.amount,
        rentCurrency: currentRentDetails.currency,
        rentInterval: validInterval,
      });
    }
  }, [currentRentDetails, rentDetailsForm]);

  useEffect(() => {
    rentDetailsForm.register('rentAmount', {
      required: 'Rent amount is required',
      min: {
        value: 1,
        message: 'Rent must be greater than 0',
      },
    });
  }, [rentDetailsForm]);

  const setRentDetailsMutation = useSetRentDetails(unitId, {
    onSuccess: () => {
      onRentUpdate(true);
    },
  });

  const handleSetRent = rentDetailsForm.handleSubmit(data => {
    const rentData: SetRentDetailsDto = {
      amount: data.rentAmount * 100, // Convert back to cents
      currency: data.rentCurrency,
      interval: data.rentInterval,
    };
    setRentDetailsMutation.mutate(rentData);
  });

  // Format date string for display
  const formatDate = (dateString: string): string =>
    new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });

  // Format currency for display
  const formatCurrency = (amount: number, isInCents = false): string =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    })
      .format(amount / (isInCents ? 100 : 1))
      .replace('$', '');

  const renderRentPriceHistory = () => {
    if (isLoadingPrices) {
      return (
        <Stack alignItems="center" justifyContent="center" height="100px">
          <CircularProgress />
        </Stack>
      );
    }
    if (rentPrices.length === 0) {
      return <Typography color="text.secondary">No price history available</Typography>;
    }
    return (
      <TableContainer
        component={Paper}
        sx={{ maxHeight: 300, boxShadow: 'none', border: '1px solid #e0e0e0' }}
      >
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell>Date Created</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell>Currency</TableCell>
              <TableCell>Interval</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rentPrices.map(price => (
              <TableRow key={price.priceId} hover>
                <TableCell>{formatDate(price.created)}</TableCell>
                <TableCell>${formatCurrency(price.amount)}</TableCell>
                <TableCell>{price.currency.toUpperCase()}</TableCell>
                <TableCell>Per {price.interval}</TableCell>
                <TableCell>
                  {price.active ? (
                    <Chip label="Current Rent" color="success" size="small" />
                  ) : (
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.secondary',
                        border: '1px solid #e0e0e0',
                        borderRadius: '16px',
                        display: 'inline-block',
                        padding: '0px 8px',
                        fontSize: '0.75rem',
                      }}
                    >
                      Inactive
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Card>
      <CardHeader title={currentRentDetails ? 'Update Monthly Rent' : 'Define Monthly Rent'} />
      <CardContent>
        {isLoadingRent ? (
          <Stack alignItems="center" justifyContent="center" minHeight="100px">
            <CircularProgress />
          </Stack>
        ) : (
          <Box component="form" onSubmit={handleSetRent} noValidate sx={{ mt: 1 }}>
            <Stack spacing={2} direction="row">
              <FormTextField
                name="rentAmount"
                label="Rent Amount"
                control={rentDetailsForm.control}
                required
                helperText={rentDetailsForm.formState.errors.rentAmount?.message}
                error={!!rentDetailsForm.formState.errors.rentAmount}
                textFieldProps={{
                  type: 'number',
                  InputProps: {
                    startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
                  },
                  sx: { width: '150px' },
                }}
              />
              {/* TODO: Add Currency and Interval Selectors - Using read-only values for now */}
              <FormTextField
                name="rentCurrency"
                label="Currency"
                control={rentDetailsForm.control}
                disabled
                textFieldProps={{ sx: { width: '100px' } }}
              />
              <FormTextField
                name="rentInterval"
                label="Interval"
                control={rentDetailsForm.control}
                disabled
                textFieldProps={{ sx: { width: '120px' } }}
              />
            </Stack>
            <Button
              type="submit"
              variant="contained"
              disabled={
                setRentDetailsMutation.isPending || !!rentDetailsForm.formState.errors.rentAmount
              }
              sx={{ mt: 3, mb: 2 }}
            >
              {setRentDetailsMutation.isPending ? <CircularProgress size={24} /> : 'Set Rent'}
            </Button>
          </Box>
        )}

        {/* Rent Price History Section */}
        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Rent Price History
          </Typography>

          {renderRentPriceHistory()}
        </Box>
      </CardContent>
    </Card>
  );
};

export default RentDetailsFormSection;
